/**
 * Tunable constants. Everything that shapes the board (decay, caps, limits) lives here so it can be
 * tuned in one place and is identical on the client and in Cloud Functions.
 */

export const DAY_MS = 86_400_000;
export const HOUR_MS = 3_600_000;

/**
 * Radius options for the location dropdown. `precision` is the geohash precision of the area
 * boards used to cover the circle (see geo.ts `coverCircle`); `km: null` is the whole world.
 */
export const RADIUS_OPTIONS = [
  { id: '10km', km: 10, precision: 4, label: 'Within 10 km' },
  { id: '100km', km: 100, precision: 3, label: 'Within 100 km' },
  { id: '1000km', km: 1000, precision: 2, label: 'Within 1,000 km' },
  { id: 'world', km: null, precision: 0, label: 'World' },
] as const;

export type RadiusOption = (typeof RADIUS_OPTIONS)[number];
export type RadiusId = RadiusOption['id'];

/**
 * How far back the board looks: posts made in the last … (24 hours unless the reader picks another).
 * The longest matches how long posts are ranked (`RANKING.maxAgeMs`).
 */
export const TIME_RANGES = [
  { id: '6h', ms: 6 * HOUR_MS, label: '6 hours', short: '6 h' },
  { id: '24h', ms: 24 * HOUR_MS, label: '24 hours', short: '24 h' },
  { id: '3d', ms: 3 * DAY_MS, label: '3 days', short: '3 days' },
  { id: '7d', ms: 7 * DAY_MS, label: '7 days', short: '7 days' },
] as const;

export type TimeRange = (typeof TIME_RANGES)[number];
export type TimeRangeId = TimeRange['id'];
export const DEFAULT_TIME_RANGE: TimeRangeId = '24h';

/** Geohash precision stored on posts: ~4.9 km x 4.9 km cells, so exact locations are never kept. */
export const POST_GEOHASH_PRECISION = 5;

/** Precisions at which area boards are precomputed (~39x20 km, ~156 km, ~1,250x625 km cells). */
export const AREA_PRECISIONS = [4, 3, 2] as const;

export const WORLD_AREA_KEY = 'world';

export const RANKING = {
  /** Engagement loses half its weight every 6 hours, so the board reflects what is hot now. */
  halfLifeMs: 6 * HOUR_MS,
  signedInWeight: 1,
  anonymousWeight: 0.5,
  /** Opens beyond this per uid per ranking run are ignored (bot dampening). */
  maxOpensPerUidPerRun: 20,
  /** Opens newer than this are left for the next run, so late-committing writes are never skipped. */
  settleMs: 5_000,
  tilesPerArea: 60,
  latestPerArea: 20,
  /** Keeps `ranking/state` well under Firestore's 1 MiB document limit (~400 bytes per post). */
  maxTrackedPosts: 1_500,
  maxAgeMs: 7 * DAY_MS,
  /** Posts that never got attention stop being tracked after this long. */
  unscoredGraceMs: DAY_MS,
  /** Decayed score below which a post leaves the board. */
  minScore: 0.05,
  /** Relative-weight granularity used to decide whether an area board changed enough to rewrite. */
  weightBucket: 0.05,
  /** Max posts that went untracked but got opened (e.g. via a shared link) to revive per run. */
  maxRevivedPerRun: 50,
} as const;

export const SIZING = {
  /** Area ∝ score^exponent: sublinear, so a hit can't eat the whole page. */
  exponent: 0.6,
  /** No single tile takes more than this share of the board (relaxed for tiny boards). */
  maxShare: 0.2,
  /** The talk of the town always gets at least this share of the board. */
  crownMinShare: 0.15,
  /**
   * An AI summary's tile weighs this much of what a person's post with the same attention gets, so
   * people's posts get more of the board. Only AI tiles shrink: nothing is blown up to make room.
   * (The talk of the town, the post opened most, is always the biggest, AI or not.)
   */
  agentWeight: 0.5,
  /** Minimum readable tile area in CSS pixels (a speech bubble with a line or two of title). */
  minTileAreaPx: 160 * 100,
} as const;

/** Characters of the description kept on boards (keeps area/state docs small). */
export const EXCERPT_MAX = 100;

export const BOARD = {
  /** Voices in the middle of the board, at most; quieter ones join the whispers around them. */
  maxVoices: 5,
  /**
   * A post is a "voice" (on the board) once its decayed score reaches this: someone opening it (a
   * visitor's open is worth 0.5). It fades back to a "whisper" (shown only when the reader listens
   * to the whispers) once nobody has opened it for a while: about an hour after one visitor's open,
   * about seven hours after one signed-in person's.
   */
  voiceMinScore: 0.45,
  /**
   * The middle never falls quiet: with fewer voices than this, the most promising whispers (most
   * opened, then newest) are shown as voices too.
   */
  minVoices: 3,
  /** New posts older than this are no longer whispered. */
  latestMaxAgeMs: 7 * DAY_MS,
} as const;

export const POST_LIMITS = {
  titleMax: 100,
  bodyMax: 2_000,
  linksMax: 1,
  linkMaxLength: 2_048,
  imagesMax: 4,
  postsPerDay: 5,
  minIntervalMs: 2 * 60_000,
  imageMaxBytes: 1024 * 1024,
  imageMaxDimension: 10_000,
} as const;

export const MODERATION = {
  /** Distinct reports that auto-hide a post until an admin reviews it. */
  reportsToHide: 3,
  /** Vision SafeSearch has 1,000 free units/month; stay safely below. */
  visionMonthlyBudget: 950,
} as const;

/** How long open (engagement) docs live before Firestore TTL deletes them. */
export const OPEN_TTL_MS = 2 * DAY_MS;
